// Base44 API Client
// This client provides methods to interact with the backend API
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach JWT token and restaurant context
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const restaurantId = localStorage.getItem('current_restaurant_id');
    if (restaurantId) {
      config.headers['X-Restaurant-Id'] = restaurantId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh queue to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor: handle 401 and refresh token
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh/`, {
            refresh: refreshToken,
          });
          const newToken = response.data.access;
          localStorage.setItem('access_token', newToken);
          if (response.data.refresh) {
            localStorage.setItem('refresh_token', response.data.refresh);
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return apiClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      isRefreshing = false;
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Agents API
const agents = {
  async listConversations(params = {}) {
    try {
      const agent_name = typeof params === 'string' ? params : params?.agent_name;
      const response = await apiClient.get(`/agents/${agent_name}/conversations/`);
      const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error listing conversations:', error);
      return [];
    }
  },
  async createConversation(params = {}) {
    try {
      const agent_name = typeof params === 'string' ? params : params?.agent_name;
      const response = await apiClient.post(`/agents/${agent_name}/conversations/`, params);
      return response.data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { id: null, messages: [] };
    }
  },
  async getConversation(id) {
    try {
      const response = await apiClient.get(`/conversations/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error getting conversation:', error);
      return { id: null, messages: [] };
    }
  },
  async addMessage(conversation, message = {}) {
    try {
      const conversationId = typeof conversation === 'object' ? conversation?.id : conversation;
      const content = typeof message === 'object' ? message?.content : message;
      const response = await apiClient.post(`/conversations/${conversationId}/messages/`, { content });
      return response.data;
    } catch (error) {
      console.error('Error adding message:', error);
      return {};
    }
  },
  subscribeToConversation(conversationId, callback) {
    // No-op — real-time not implemented
    return () => {};
  },
  async transcribe(audioBlob, language) {
    try {
      const formData = new FormData();
      const ext = audioBlob.type?.includes('webm') ? 'webm' : 'mp4';
      formData.append('audio', audioBlob, `recording.${ext}`);
      if (language) formData.append('language', language);
      const response = await apiClient.post('/agents/transcribe/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  },
};

// Helper function to create CRUD operations for entities
const createEntityCRUD = (endpoint, defaultOrderBy = '-id') => ({
  async list(orderBy = defaultOrderBy) {
    try {
      const response = await apiClient.get(`/${endpoint}/`, {
        params: { ordering: orderBy },
      });
      const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(`Error listing ${endpoint}:`, error);
      return [];
    }
  },
  async create(data) {
    try {
      const response = await apiClient.post(`/${endpoint}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${endpoint}:`, error);
      throw error;
    }
  },
  async update(id, data) {
    try {
      const response = await apiClient.patch(`/${endpoint}/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${endpoint}:`, error);
      throw error;
    }
  },
  async delete(id) {
    try {
      const response = await apiClient.delete(`/${endpoint}/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting ${endpoint}:`, error);
      throw error;
    }
  },
});

// Helper function to add bulkCreate to entities
const addBulkCreate = (entityCRUD, endpoint) => ({
  ...entityCRUD,
  async bulkCreate(itemsData) {
    try {
      // Create items one by one (or implement bulk endpoint if backend supports it)
      const results = await Promise.all(
        itemsData.map(item => apiClient.post(`/${endpoint}/`, item))
      );
      return results.map(r => r.data);
    } catch (error) {
      console.error(`Error bulk creating ${endpoint}:`, error);
      throw error;
    }
  },
});

// Entities API
const entities = {
  Sale: addBulkCreate(createEntityCRUD('sales', '-date'), 'sales'),

  InventoryItem: addBulkCreate(createEntityCRUD('inventory', '-id'), 'inventory'),

  Supplier: createEntityCRUD('suppliers', 'name'),

  Task: addBulkCreate(createEntityCRUD('tasks', '-due_date'), 'tasks'),

  // Employee entity with bulkCreate
  Employee: addBulkCreate(createEntityCRUD('employees', '-id'), 'employees'),

  // Shift entity
  Shift: createEntityCRUD('shifts', '-date'),

  // Event entity
  Event: createEntityCRUD('events', '-date'),

  // Expense entity
  Expense: createEntityCRUD('expenses', '-date'),

  // ProductionLoss entity
  ProductionLoss: createEntityCRUD('production-losses', '-date'),

  Order: addBulkCreate(createEntityCRUD('orders', '-order_date'), 'orders'),

  // Restaurant entity
  Restaurant: createEntityCRUD('restaurants', '-id'),

  // Recipe entity with bulkCreate
  Recipe: addBulkCreate(createEntityCRUD('recipes', '-id'), 'recipes'),

  // ProductionBatch entity
  ProductionBatch: createEntityCRUD('production-batches', '-production_date'),

  // POS Integrations
  POSIntegration: createEntityCRUD('pos/integrations', 'provider'),
};

// Task-Recipe Allocations API
const taskRecipeAllocations = {
  listAll: async () => {
    const response = await apiClient.get('/task-recipe-allocations/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  listByTask: async (taskId) => {
    const response = await apiClient.get('/task-recipe-allocations/', { params: { task: taskId } });
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  listByRecipe: async (recipeId) => {
    const response = await apiClient.get('/task-recipe-allocations/', { params: { recipe: recipeId } });
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  create: async (data) => {
    const response = await apiClient.post('/task-recipe-allocations/', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.patch(`/task-recipe-allocations/${id}/`, data);
    return response.data;
  },
  delete: async (id) => {
    await apiClient.delete(`/task-recipe-allocations/${id}/`);
  },
};

// POS-specific API calls
const pos = {
  getIntegrations: async () => {
    try {
      const response = await apiClient.get('/pos/integrations/');
      const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error fetching POS integrations:', error);
      return [];
    }
  },
  syncSales: async (integrationId) => {
    const response = await apiClient.post(`/pos/sync/${integrationId}/sales/`);
    return response.data;
  },
  syncMenu: async (integrationId) => {
    const response = await apiClient.post(`/pos/sync/${integrationId}/menu/`);
    return response.data;
  },
  getLogs: async (integrationId) => {
    const response = await apiClient.get(`/pos/logs/${integrationId}/`);
    return response.data;
  },
  startSumUpOAuth: (restaurantId) => {
    window.location.href = `${API_URL}/pos/oauth/sumup/authorize/?restaurant_id=${restaurantId}`;
  },
  startLightspeedOAuth: (restaurantId) => {
    window.location.href = `${API_URL}/pos/oauth/lightspeed/authorize/?restaurant_id=${restaurantId}`;
  },
  createIntegration: async (provider) => {
    const response = await apiClient.post('/pos/integrations/', { provider });
    return response.data;
  },
  uploadOrderbirdCSV: async (integrationId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/pos/orderbird/${integrationId}/upload-csv/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },
  disconnect: async (integrationId) => {
    const response = await apiClient.patch(`/pos/integrations/${integrationId}/`, { is_active: false });
    return response.data;
  },
  saveCredentials: async (provider, clientId, clientSecret) => {
    const response = await apiClient.post(`/pos/credentials/${provider}/`, {
      client_id: clientId,
      client_secret: clientSecret,
    });
    return response.data;
  },
  deleteCredentials: async (provider) => {
    const response = await apiClient.delete(`/pos/credentials/${provider}/`);
    return response.data;
  },
};

// Organizations API
const organizations = {
  listUsers: async () => {
    const response = await apiClient.get('/organizations/users/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  updateUser: async (id, data) => {
    const response = await apiClient.patch(`/organizations/users/${id}/`, data);
    return response.data;
  },
  deactivateUser: async (id) => {
    await apiClient.delete(`/organizations/users/${id}/`);
  },
  activateUser: async (id) => {
    const response = await apiClient.patch(`/organizations/users/${id}/`, { is_active: true });
    return response.data;
  },
  deleteUser: async (id) => {
    await apiClient.delete(`/organizations/users/${id}/hard-delete/`);
  },
  listInvitations: async () => {
    const response = await apiClient.get('/organizations/invitations/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
  createInvitation: async (email) => {
    const response = await apiClient.post('/organizations/invitations/', { email });
    return response.data;
  },
  deleteInvitation: async (id) => {
    await apiClient.delete(`/organizations/invitations/${id}/`);
  },
  verifyInvite: async (token) => {
    const response = await apiClient.get(`/auth/invite/${token}/verify/`);
    return response.data;
  },
  acceptInvite: async (token, data) => {
    const response = await apiClient.post(`/auth/invite/${token}/accept/`, data);
    return response.data;
  },
};

const tasks = {
  assign: async (taskId, userIds, dueDate) => {
    const response = await apiClient.post(`/tasks/${taskId}/assign/`, {
      user_ids: userIds,
      ...(dueDate && { due_date: dueDate }),
    });
    return response.data;
  },
  ranking: async () => {
    const response = await apiClient.get('/tasks/ranking/');
    const result = response.data?.results ?? response.data;
      return Array.isArray(result) ? result : [];
  },
};

// Forecast API
const forecast = {
  getGuests: async (days = 7) => {
    try {
      const response = await apiClient.get('/forecast/guests/', { params: { days } });
      return response.data;
    } catch (error) {
      console.error('Error fetching guest forecast:', error);
      return { forecasts: [], meta: {} };
    }
  },
  syncWeather: async () => {
    try {
      const response = await apiClient.get('/forecast/weather/sync/');
      return response.data;
    } catch (error) {
      console.error('Error syncing weather:', error);
      return { backfill_records: 0, forecast_records: 0, errors: [] };
    }
  },
};

export const base44 = {
  agents,
  entities,
  pos,
  organizations,
  taskRecipeAllocations,
  tasks,
  forecast,
};

